// ===========================================
// TypesAssurance — Page liste des types d'assurance (refonte UX)
// ===========================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, InputNumber, Button, Grid, Modal, Form, Popconfirm,
    Space, Tooltip, Tag, Empty,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
    SafetyCertificateOutlined, IdcardOutlined, PercentageOutlined, BankOutlined,
} from '@ant-design/icons';
import { typesAssuranceService } from '../../common/services';

import MiniKpiCard from '../clients/components/MiniKpiCard';
import Can from '../../components/can';

import './types-assurance.page.scss';

const TypesAssurance = () => {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const fetchTypes = async () => {
        setLoading(true);
        try {
            const res = await typesAssuranceService.getAll();
            const data = Array.isArray(res) ? res : res?.data || [];
            setTypes(data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTypes(); }, []);

    const filteredTypes = useMemo(() => {
        if (!search) return types;
        const q = search.toLowerCase();
        return types.filter((t) =>
            `${t.nom || ''} ${t.code || ''} ${t.description || ''}`.toLowerCase().includes(q)
        );
    }, [types, search]);

    const stats = useMemo(() => {
        const total = types.length;
        const tauxGlobal = types.filter((t) => t.tauxTaxe == null).length;
        const commMoyenne = total > 0
            ? Math.round((types.reduce((s, t) => s + (t.tauxCommission || 0), 0) / total) * 100) / 100
            : 0;
        return { total, tauxGlobal, commMoyenne };
    }, [types]);

    const handleSave = async (values) => {
        try {
            setModalLoading(true);
            if (editingItem) {
                await typesAssuranceService.update(editingItem._id, values);
                toast.success('Type mis à jour');
            } else {
                await typesAssuranceService.create(values);
                toast.success('Type créé');
            }
            setModalVisible(false);
            setEditingItem(null);
            form.resetFields();
            fetchTypes();
        } catch (error) {
            toast.error(error.message || 'Erreur');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await typesAssuranceService.delete(id);
            toast.success('Type supprimé');
            fetchTypes();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const openCreateModal = () => {
        setEditingItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    const columns = [
        {
            title: 'Nom du type',
            dataIndex: 'nom',
            key: 'nom',
            sorter: (a, b) => (a.nom || '').localeCompare(b.nom || ''),
            render: (t) => (
                <div className="ta-cell">
                    <div className="ta-icon"><SafetyCertificateOutlined /></div>
                    <span className="ta-name">{t || '—'}</span>
                </div>
            ),
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 140,
            render: (t) => t
                ? <Tag color="geekblue" className="ta-code-tag">{t}</Tag>
                : <span className="ta-muted">—</span>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (t) => t || <span className="ta-muted">—</span>,
        },
        {
            title: 'Taux commission',
            dataIndex: 'tauxCommission',
            key: 'taux',
            width: 160,
            align: 'right',
            sorter: (a, b) => (a.tauxCommission || 0) - (b.tauxCommission || 0),
            render: (v) => (
                <span className={`ta-rate ${v > 0 ? 'is-positive' : ''}`}>
                    {(v || 0)}<span className="ta-rate-pct">%</span>
                </span>
            ),
        },
        {
            title: 'Taux taxe',
            dataIndex: 'tauxTaxe',
            key: 'tauxTaxe',
            width: 140,
            align: 'right',
            render: (v) => v != null
                ? <span className="ta-rate">{v}<span className="ta-rate-pct">%</span></span>
                : <Tag color="default" className="ta-global-tag">Global</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            align: 'center',
            fixed: isMobile ? false : 'right',
            render: (_, record) => (
                <Space size={6}>
                    <Can perm="typesAssurance:update">
                        <Tooltip title="Modifier">
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                className="ta-action-btn ta-action-edit"
                                aria-label="Modifier ce type"
                                onClick={() => handleEdit(record)}
                            />
                        </Tooltip>
                    </Can>
                    <Can perm="typesAssurance:delete">
                        <Popconfirm
                            title="Supprimer ce type ?"
                            description="Cette action est irréversible."
                            okText="Oui"
                            cancelText="Non"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record._id)}
                        >
                            <Tooltip title="Supprimer">
                                <Button
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    className="ta-action-btn ta-action-delete"
                                    aria-label="Supprimer ce type"
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Can>
                </Space>
            ),
        },
    ];

    return (
        <div className="types-assurance-page">
            {/* Header */}
            <header className="ta-header">
                <div className="ta-header-text">
                    <h1 className="ta-title">
                        <SafetyCertificateOutlined /> Types d'assurance
                    </h1>
                    <p className="ta-subtitle">Catalogue des produits d'assurance et taux par défaut</p>
                </div>
                <div className="ta-header-actions">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        className="ta-btn-add"
                    >
                        {!isMobile && 'Nouveau type'}
                    </Button>
                </div>
            </header>

            {/* KPIs */}
            <section className="ta-kpis" aria-label="Indicateurs types d'assurance">
                <MiniKpiCard
                    icon={<SafetyCertificateOutlined />}
                    label="Total des types"
                    value={stats.total}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<PercentageOutlined />}
                    label="Commission moyenne"
                    value={`${stats.commMoyenne}%`}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<BankOutlined />}
                    label="Taxe globale"
                    value={stats.tauxGlobal}
                    tone="indigo"
                />
            </section>

            {/* Carte principale */}
            <Card className="ta-main-card">
                <div className="ta-toolbar">
                    <Input
                        placeholder="Rechercher un type (nom, code, description)…"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="ta-search"
                    />
                </div>

                <Table
                    dataSource={filteredTypes}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucun type d'assurance" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        pageSize: 15,
                        showTotal: (t) => `${t} type${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                />
            </Card>

            {/* Modal créer / modifier */}
            <Modal
                title={
                    <span className="ta-modal-title">
                        <SafetyCertificateOutlined /> {editingItem ? "Modifier le type d'assurance" : "Nouveau type d'assurance"}
                    </span>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                destroyOnClose
                width={520}
                className="ta-modal"
            >
                <Form form={form} layout="vertical" onFinish={handleSave} className="ta-form">
                    <Form.Item
                        name="nom"
                        label="Nom du type"
                        rules={[{ required: true, message: 'Le nom du type est obligatoire' }]}
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input
                            autoFocus
                            prefix={<SafetyCertificateOutlined />}
                            placeholder="Ex : Assurance Automobile / Moto"
                        />
                    </Form.Item>
                    <Form.Item
                        name="code"
                        label="Code"
                        rules={[{ required: true, message: 'Le code est obligatoire' }]}
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input
                            prefix={<IdcardOutlined />}
                            placeholder="Ex : AUTO, VIE, HABITATION…"
                        />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input placeholder="Description optionnelle" />
                    </Form.Item>
                    <Form.Item
                        name="tauxCommission"
                        label="Taux de commission par défaut (%)"
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            style={{ width: '100%' }}
                            placeholder="0"
                        />
                    </Form.Item>
                    <Form.Item
                        name="tauxTaxe"
                        label="Taux de taxe (%)"
                        extra="Laisser vide pour appliquer le taux global"
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            style={{ width: '100%' }}
                            placeholder="Vide = taux global"
                        />
                    </Form.Item>
                    <div className="ta-form-actions">
                        <Button onClick={() => setModalVisible(false)} className="ta-btn-cancel">
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={modalLoading}
                            className="ta-btn-submit"
                        >
                            {editingItem ? 'Enregistrer' : 'Créer le type'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default TypesAssurance;
